/**
 * Heuristic Engine — typed hooks for the Phase 2d admin surface.
 *
 * Endpoints (all under /api/v2/, gated to internal roles):
 *   GET  /admin/heuristic/stats         — observability tile data
 *   POST /admin/heuristic/test          — score an indicator + enqueue async deep run
 *   GET  /admin/heuristic/queue/{id}    — poll a queue row's lifecycle
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import { api } from "./api";

export interface HeuristicStats {
    generatedAt: string;
    queueDepth: number;
    queueRunning: number;
    verdicts24h: number;
    calibrations24h: number;
    agreementRate24h: number;
    latencyP50Ms: number;
    latencyP99Ms: number;
    tier2TriggerRate: number;
    hotVerdicts: number;
    promotedIocs24h: number;
}

export interface HeuristicFactor {
    name: string;
    weight: number;
    value: string;
    contribution: number;
}

export interface HeuristicVerdict {
    score: number;
    decision: string;
    factors: HeuristicFactor[] | null;
}

export interface HeuristicTestResponse {
    indicator: string;
    indicatorType: string;
    verdict: HeuristicVerdict;
    queueId: string;
    workflowId?: string;
    enqueuedAt: string;
    cacheHit: boolean;
}

export interface HeuristicQueueRow {
    id: string;
    indicatorValue: string;
    indicatorType: string;
    status: string;
    staticScore?: number;
    deepScore?: number;
    workflowId?: string;
    enqueuedAt: string;
    completedAt?: string;
    latencyMs?: number;
    result?: Record<string, unknown>;
}

export function useHeuristicStats(opts?: { enabled?: boolean }) {
    return useQuery({
        queryKey: ["heuristic", "stats"],
        queryFn: () => api.get<HeuristicStats>("/api/v2/admin/heuristic/stats"),
        refetchInterval: 10_000,
        enabled: opts?.enabled ?? true,
    });
}

export function useHeuristicTest() {
    return useMutation({
        mutationFn: (input: { indicator: string; indicatorType?: string }) =>
            api.post<HeuristicTestResponse>("/api/v2/admin/heuristic/test", input),
    });
}

// ----- Phase F.5 — fuzzy-hash similarity (admin proxy) ----------------------

export interface SimilarityTestRequest {
    sha256?: string;
    tlsh?: string;
    ssdeep?: string;
    imphash?: string;
}

export interface SimilarityTestMatch {
    iocId?: string;
    sha256: string;
    tlsh?: string;
    ssdeep?: string;
    imphash?: string;
    distance?: number;
    ssdeepScore?: number;
    reason: string;
    strength: string; // "strong" | "medium" | "weak" | "none"
    severity: number;
    tags: string[];
    evidenceUrl?: string;
}

export interface SimilarityTestResponse {
    decision: string;
    strategy: string;
    matches: SimilarityTestMatch[];
    generation: number;
}

export function useSimilarityTest() {
    return useMutation({
        mutationFn: (input: SimilarityTestRequest) =>
            api.post<SimilarityTestResponse>("/api/v2/admin/similarity/test", input),
    });
}

/**
 * Polls a queue row every 1.5s until it reaches a terminal status. Caller
 * passes `enabled: false` to stop the poll (e.g. after success / on unmount).
 */
export function useHeuristicQueueRow(id: string | null, opts?: { enabled?: boolean }) {
    return useQuery({
        queryKey: ["heuristic", "queue", id],
        queryFn: () => api.get<HeuristicQueueRow>(`/api/v2/admin/heuristic/queue/${id}`),
        enabled: !!id && (opts?.enabled ?? true),
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            if (status === "done" || status === "failed" || status === "cancelled") {
                return false;
            }
            return 1500;
        },
    });
}
